import { createFileRoute } from '@tanstack/react-router';
import { ImageIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useRef, useState } from 'react';

import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { PageShell } from '@/components/app/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  useBrandExamples,
  useCreateBrandExample,
  useDeleteBrandExample,
  useSetBrandExampleDisabled,
} from '@/features/brand-examples/api';

export const Route = createFileRoute('/_authed/dashboard/settings/examples')({
  component: ExamplesSettingsPage,
});

function ExamplesSettingsPage() {
  const { examples, isLoading } = useBrandExamples();
  const { createExample, isCreating } = useCreateBrandExample();
  const { setDisabled } = useSetBrandExampleDisabled();
  const { deleteExample, isDeleting } = useDeleteBrandExample();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const onFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      createExample({ imageBase64: dataUrl.split(',')[1] });
    };
    reader.readAsDataURL(file);
  };

  return (
    <PageShell
      className="flex flex-col gap-6 pt-10 pb-8"
      maxWidth="max-w-[960px]"
    >
      <title>Examples | Borrah</title>
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Style examples</CardTitle>
            <CardDescription>
              Posts your generated carousels copy for style — colours, fonts and
              your logo come from these. Upload finished posts you love; one is
              used per generation.
            </CardDescription>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => {
              onFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <Button
            size="sm"
            disabled={isCreating}
            onClick={() => fileRef.current?.click()}
          >
            <PlusIcon />
            {isCreating ? 'Uploading…' : 'Add example'}
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Skeleton className="aspect-square" />
              <Skeleton className="aspect-square" />
              <Skeleton className="aspect-square" />
            </div>
          ) : examples.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <ImageIcon />
                </EmptyMedia>
                <EmptyTitle>No examples yet</EmptyTitle>
                <EmptyDescription>
                  Without an example, carousels are designed freely. Add a post
                  whose look you want copied — including your logo.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {examples.map((example, index) => (
                <div
                  key={example.id}
                  className="group relative overflow-hidden rounded-md border"
                >
                  <img
                    src={example.imageUrl}
                    alt={example.notes ?? 'Style example'}
                    className="aspect-square w-full object-cover"
                  />
                  {example.disabled && (
                    <Badge
                      variant="secondary"
                      className="absolute top-2 left-2 opacity-90"
                    >
                      Disabled
                    </Badge>
                  )}
                  <div className="flex items-center justify-between gap-2 border-t bg-card p-2">
                    <span className="truncate text-muted-foreground text-xs">
                      {example.notes ?? 'Example'}
                    </span>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!example.disabled}
                        onCheckedChange={(enabled) =>
                          setDisabled({ id: example.id, disabled: !enabled })
                        }
                        aria-label="Use this example"
                      />
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Delete ${example.notes ?? `example ${index + 1}`}`}
                        onClick={() => setDeletingId(example.id)}
                      >
                        <Trash2Icon className="size-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <ConfirmDeleteDialog
        open={deletingId !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingId(null);
        }}
        title="Delete this style example?"
        description="This example will be removed from Settings and will no longer be used for new graphics."
        isPending={isDeleting}
        onConfirm={() => {
          if (deletingId)
            deleteExample(deletingId, {
              onSuccess: () => setDeletingId(null),
            });
        }}
      />
    </PageShell>
  );
}
